import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import { getDaysText } from '../../utils/formatters';
import '../../styles/GracePeriodWarning.css';

// Session storage key for dismissed state
const DISMISSED_KEY = 'gracePeriodWarningDismissed';

/**
 * GracePeriodWarning Component
 *
 * Shows a warning banner when user is in grace period.
 * Appears when:
 * - Subscription has expired
 * - But user is still within the grace period
 *
 * Can be dismissed once per session.
 */
const GracePeriodWarning = () => {
    const { t } = useTranslation('subscription');
    const navigate = useNavigate();
    const [isDismissed, setIsDismissed] = useState(false);

    const {
        isInGracePeriod,
        getGracePeriodEndDate,
        formatDate
    } = useSubscription();

    // Check dismissed state on mount
    useEffect(() => {
        const wasDismissed = sessionStorage.getItem(DISMISSED_KEY) === 'true';
        setIsDismissed(wasDismissed);
    }, []);

    // Don't render if not in grace period or dismissed
    if (!isInGracePeriod() || isDismissed) {
        return null;
    }

    const gracePeriodEnd = getGracePeriodEndDate();
    const formattedDate = formatDate(gracePeriodEnd);

    // Calculate days remaining
    const daysRemaining = gracePeriodEnd
        ? Math.ceil((new Date(gracePeriodEnd) - new Date()) / (1000 * 60 * 60 * 24))
        : 0;

    const handleDismiss = () => {
        setIsDismissed(true);
        sessionStorage.setItem(DISMISSED_KEY, 'true');
    };

    const handleUpdatePayment = () => {
        navigate('/subscription/plans');
    };

    return (
        <div className="grace-period-warning">
            <div className="grace-period-warning-content">
                <div className="warning-icon">⚠️</div>

                <div className="warning-text">
                    <strong>{t('gracePeriod.title')}</strong>
                    <p>
                        {t('gracePeriod.message')}
                        {daysRemaining > 0 ? (
                            <>
                                {' '}{t('gracePeriod.accessBlocked')}{' '}
                                <strong>
                                    {daysRemaining} {getDaysText(daysRemaining)}
                                </strong>
                                {' '}({formattedDate}).
                            </>
                        ) : (
                            <> {t('gracePeriod.accessBlockedToday', { date: formattedDate })}</>
                        )}
                        {' '}{t('gracePeriod.updatePayment')}
                    </p>
                </div>

                <div className="warning-actions">
                    <button
                        className="update-payment-btn"
                        onClick={handleUpdatePayment}
                    >
                        {t('gracePeriod.updateButton')}
                    </button>
                    <button
                        className="dismiss-btn"
                        onClick={handleDismiss}
                        aria-label={t('gracePeriod.dismissLabel')}
                    >
                        ×
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GracePeriodWarning;
